import { useMemo } from 'react';
import { useAuthStore, decodeJwtPayload, extractRoles } from '@/stores/authStore';
import { permissionsForRoles, type Permission } from '@/auth/permissions';
import { canonicalRoles, isTenantAdmin as rolesAreTenantAdmin } from '@/auth/roles';
import type { UserInfo } from '@/types';

/**
 * The raw role-claim array for the signed-in user.
 *
 * Read from the TOKEN, because that is what the server evaluates on every request and because
 * `UserInfo.role` keeps only the first role. Falls back to `UserInfo.role` (a subset of the same
 * claim, captured at login) only when the token is absent or will not decode. A subset can only
 * ever produce a subset of the permissions, so the fallback cannot grant more than the token does.
 * No user and no token: no roles, so no permissions (fail closed).
 */
export function resolveRoles(token: string | null, user: UserInfo | null): string[] {
  if (token) {
    try {
      const fromToken = extractRoles(decodeJwtPayload(token));
      if (fromToken.length > 0) return fromToken;
    } catch {
      /* undecodable token: fall through to the login-time role */
    }
  }
  return user?.role ? [user.role] : [];
}

/**
 * Role/permission check for the console, mirroring admin-portal's `usePermission`.
 *
 *   const { hasPermission } = usePermission();
 *   if (hasPermission('workspace.handle')) { ...offer the action... }
 *
 * `roles` are the token's roles in CANONICAL form (`senior_agent` -> `seniorAgent`), for the folder helpers
 * (`console/folders.ts`) that take roles; `isTenantAdmin` is true only for `admin` / `platformAdmin` (D2).
 *
 * A hidden control is UX, not authorization: the server re-checks the same permission per request.
 */
export function usePermission() {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);

  const permissions = useMemo<Set<Permission>>(
    () => permissionsForRoles(resolveRoles(token, user)),
    [token, user],
  );

  const roles = useMemo<string[]>(() => canonicalRoles(resolveRoles(token, user)), [token, user]);

  const hasPermission = (permission: Permission): boolean => permissions.has(permission);

  // ADR-033 D2: `admin` and `platformAdmin` only. A senior agent holds workspace.viewAll and is NOT one.
  const isTenantAdmin = rolesAreTenantAdmin(roles);

  return { permissions, hasPermission, roles, isTenantAdmin };
}
